import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { Dayjs } from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import React, { useCallback, useEffect, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { MaterialRead } from "src/store/api/generatedApi";
import { useSubmitEvents } from "../hooks/useSubmitEvents";
import { ScrapEyesEventCard } from "./ScrapEyesEventCard";

interface ScrapEyesEventsPanelProps {
  events: ScrapEyesEvent[];
  selectedEventId: string | null;
  recordingEventId: string | null;
  onSelectEvent: (id: string | null) => void;
  onDeleteEvent?: (id: string) => void;
  onUpdateEvent?: (
    id: string,
    updates: {
      comment?: string;
      material: string | null;
      area?: string | null;
      mass?: string | null;
    }
  ) => void;
  materialOptions: MaterialRead[];
  title?: string;
  headerActions?: React.ReactNode;
  getAreaMaterialMappingAtTime?: (timestamp: Dayjs) => Record<string, string>;
  onSubmitSuccess?: () => void;
  getElapsedSeconds?: () => number;
  resetTimer?: () => void;
  location: string;
}

const ScrapEyesEventsPanelInner = ({
  events,
  selectedEventId,
  recordingEventId,
  onSelectEvent,
  onDeleteEvent,
  onUpdateEvent,
  materialOptions,
  title,
  headerActions,
  getAreaMaterialMappingAtTime,
  onSubmitSuccess,
  getElapsedSeconds,
  resetTimer,
  location,
}: ScrapEyesEventsPanelProps) => {
  const { t } = useTenantTranslation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (recordingEventId && scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }
  }, [recordingEventId]);

  useEffect(() => {
    if (selectedEventId && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const card = container.querySelector(
        `[data-event-id="${selectedEventId}"]`
      );
      if (card instanceof HTMLElement) {
        const cardRect = card.getBoundingClientRect();
        const containerRect = container.getBoundingClientRect();
        const cardTop = cardRect.top - containerRect.top + container.scrollTop;
        const cardBottom = cardTop + cardRect.height;
        const scrollTop = container.scrollTop;
        const scrollBottom = scrollTop + container.clientHeight;

        if (cardTop < scrollTop) {
          container.scrollTo({ top: cardTop, behavior: "smooth" });
        } else if (cardBottom > scrollBottom) {
          container.scrollTo({
            top: cardBottom - container.clientHeight,
            behavior: "smooth",
          });
        }
      }
    }
  }, [selectedEventId]);

  const noop = useCallback(() => undefined, []);
  const { submitEvents, isSubmitting } = useSubmitEvents({
    onSuccess: onSubmitSuccess,
    getElapsedSeconds: getElapsedSeconds ?? (() => 0),
    resetTimer: resetTimer ?? noop,
  });
  const { getHotkey } = useHotkeyContext();

  const isReadOnly = !onDeleteEvent || !onUpdateEvent;

  const getMappedMaterialForEvent = useCallback(
    (event: ScrapEyesEvent): string | null => {
      if (
        event.eventType !== "lift" ||
        !event.area ||
        !getAreaMaterialMappingAtTime
      ) {
        return null;
      }
      const mapping = getAreaMaterialMappingAtTime(event.start);
      return mapping[event.area] ?? null;
    },
    [getAreaMaterialMappingAtTime]
  );

  const submitEventsKey = getHotkey("submitEvents");
  useHotkeys(
    submitEventsKey,
    () => {
      if (!isReadOnly) {
        void submitEvents(events);
      }
    },
    {
      keydown: true,
      enabled: !isReadOnly && !isSubmitting,
    },
    [submitEventsKey, isReadOnly, submitEvents, events, isSubmitting]
  );

  return (
    <Box
      sx={{
        minWidth: 200,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack direction="row" alignItems="center" sx={{ mb: 2, flexShrink: 0 }}>
        <Typography variant="h5" color="primary">
          {title ?? t("scrapEyesEventsTitle")}
        </Typography>
        {headerActions}
      </Stack>

      <Stack
        ref={scrollContainerRef}
        sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
      >
        {events.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              {t("scrapEyesNoEvents")}
            </Typography>
          </Paper>
        ) : (
          events.map((event, index) => {
            const nextEvent = events[index + 1];
            const isPairedWithNext =
              nextEvent && event.pairId && event.pairId === nextEvent.pairId;

            return (
              <Box key={event.id} sx={{ position: "relative" }}>
                <ScrapEyesEventCard
                  event={event}
                  selectedEventId={selectedEventId}
                  recordingEventId={recordingEventId}
                  onSelectEvent={onSelectEvent}
                  onDeleteEvent={onDeleteEvent}
                  onUpdateEvent={onUpdateEvent}
                  materialOptions={materialOptions}
                  mappedMaterial={getMappedMaterialForEvent(event)}
                  getAreaMaterialMappingAtTime={getAreaMaterialMappingAtTime}
                  location={location}
                />
                {isPairedWithNext ? (
                  <Box
                    sx={{
                      position: "absolute",
                      left: "50%",
                      bottom: 0,
                      width: "4px",
                      height: "16px",
                      backgroundColor: "rgba(156, 39, 176, 0.6)",
                      zIndex: 2,
                    }}
                  />
                ) : null}
              </Box>
            );
          })
        )}
      </Stack>
      {!isReadOnly && (
        <Button
          variant="contained"
          color="primary"
          fullWidth
          loading={isSubmitting}
          onClick={() => void submitEvents(events)}
          sx={{ flexShrink: 0 }}
        >
          {t("submitEvents")}
        </Button>
      )}
    </Box>
  );
};

export const ScrapEyesEventsPanel = React.memo(ScrapEyesEventsPanelInner);
